import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Hover-only preview tooltip showing the dynamic OG share-card for an app.
 * - Mounts on hover, unmounts after 200ms grace period when leaving.
 * - Smart positioning: picks the side (above/below/right) with most space.
 * - Lazy: the <img> is only created when the tooltip mounts.
 * - Disabled on touch devices (where hover is a tap-and-hold).
 */
const OgPreviewTooltip = ({ children, slug, className = "" }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [shown, setShown] = useState(false); // controls fade-in/out
  const [coords, setCoords] = useState({ top: 0, left: 0, side: "below" });
  const hoverTimer = useRef(null);
  const hideTimer = useRef(null);
  const rafId = useRef(null);

  const compute = () => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const W = 480, H = 252, LABEL_H = 28, pad = 12;
    const vw = window.innerWidth, vh = window.innerHeight;

    const fitsBelow = rect.bottom + H + LABEL_H + pad <= vh;
    const fitsAbove = rect.top - H - LABEL_H - pad >= 0;
    const side = fitsBelow ? "below" : (fitsAbove ? "above" : "below");

    let left = rect.left + rect.width / 2 - W / 2;
    left = Math.max(pad, Math.min(left, vw - W - pad));
    const top = side === "above" ? rect.top - H - LABEL_H - pad : rect.bottom + pad;
    setCoords({ top, left, side });
  };

  const onEnter = () => {
    if (window.matchMedia?.("(hover: none)").matches) return; // skip touch
    if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; }
    if (visible) {
      // Re-enter while still mounted (fading out) — restore visible state
      setShown(true);
      return;
    }
    hoverTimer.current = setTimeout(() => {
      compute();
      setVisible(true);
    }, 220);
  };
  const onLeave = () => {
    if (hoverTimer.current) { clearTimeout(hoverTimer.current); hoverTimer.current = null; }
    // fade out first, then unmount
    setShown(false);
    hideTimer.current = setTimeout(() => setVisible(false), 220);
  };

  // After mount: trigger fade-in on next frame
  useEffect(() => {
    if (visible) {
      rafId.current = requestAnimationFrame(() => setShown(true));
      return () => {
        if (rafId.current) cancelAnimationFrame(rafId.current);
      };
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const onScroll = () => compute();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", compute);
    };
  }, [visible]);

  return (
    <>
      <div
        ref={ref}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        className={className}
        data-testid={`og-preview-trigger-${slug}`}
      >
        {children}
      </div>
      {visible && createPortal(
        <div
          style={{
            position: "fixed",
            top: coords.top,
            left: coords.left,
            width: 480,
            zIndex: 9999,
            opacity: shown ? 1 : 0,
            transform: shown
              ? "translateY(0) scale(1)"
              : `translateY(${coords.side === "above" ? "6px" : "-6px"}) scale(0.985)`,
            transition: "opacity 200ms ease-out, transform 220ms cubic-bezier(0.16, 1, 0.3, 1)",
            willChange: "opacity, transform",
          }}
          className="pointer-events-none"
          data-testid={`og-preview-${slug}`}
        >
          <div className="rounded-xl overflow-hidden shadow-2xl border-2 border-white/20 ring-1 ring-black/5 bg-[#0F172A]" style={{ width: 480, height: 252 }}>
            <img
              src={`${BACKEND}/api/og/app/${slug}.png`}
              alt=""
              width={480}
              height={252}
              loading="lazy"
              className="block object-cover"
              style={{ width: 480, height: 252 }}
              draggable={false}
            />
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 mt-2 text-center font-mono">Share preview</div>
        </div>,
        document.body
      )}
    </>
  );
};

export default OgPreviewTooltip;
